/**
 * API Logger для мониторинга запросов
 * Event Master v1.5
 */

import type { AxiosRequestConfig, AxiosResponse, AxiosError } from "axios";
import { config } from "../config/env";
import { hasAnalyticsConsent } from "./cookie-consent";

interface ApiCallMetrics {
  method?: string | undefined;
  url?: string | undefined;
  status?: number | undefined;
  duration?: number | undefined;
  error?: boolean | undefined;
  errorMessage?: string | undefined;
  timestamp: string;
}

/**
 * Логирование API вызова
 */
export const logApiCall = (
  requestConfig: AxiosRequestConfig,
  response?: AxiosResponse,
  error?: AxiosError,
): void => {
  const metrics: ApiCallMetrics = {
    method: requestConfig.method?.toUpperCase(),
    url: requestConfig.url,
    status: response?.status || error?.response?.status,
    timestamp: new Date().toISOString(),
  };

  // Расчет продолжительности (если доступно)
  const metadata = (requestConfig as any).metadata;
  if (metadata?.startTime && metadata?.endTime) {
    metrics.duration = metadata.endTime - metadata.startTime;
  }

  // Обработка ошибки
  if (error) {
    metrics.error = true;
    metrics.errorMessage = error.message;
  }

  // Логирование в консоль (только в development)
  if (process.env["NODE_ENV"] === "development") {
    const emoji = error ? "❌" : "✅";
    // eslint-disable-next-line no-console
    console.log(`${emoji} API Call:`, {
      ...metrics,
      duration: metrics.duration ? `${metrics.duration}ms` : "N/A",
    });
  }

  // Отправка в систему мониторинга (Sentry, LogRocket, etc.)
  if (config.LOGGING.ERROR_REPORTING && error) {
    sendToMonitoring(metrics, error);
  }

  // Performance monitoring
  if (config.LOGGING.PERFORMANCE_MONITORING && metrics.duration) {
    trackPerformance(metrics);
  }
};

/**
 * Отправка метрик в Sentry (или другую систему мониторинга)
 */
const sendToMonitoring = (metrics: ApiCallMetrics, error: AxiosError): void => {
  try {
    // Проверяем, доступен ли Sentry
    if (typeof window !== "undefined" && (window as any).Sentry) {
      const Sentry = (window as any).Sentry;

      Sentry.captureException(error, {
        tags: {
          api: true,
          method: metrics.method,
          status: metrics.status,
        },
        extra: {
          url: metrics.url,
          duration: metrics.duration,
          requestData: error.config?.data,
          responseData: error.response?.data,
        },
        level: metrics.status && metrics.status >= 500 ? "error" : "warning",
      });
    }
  } catch (monitoringError) {
    console.warn("Failed to send metrics to monitoring:", monitoringError);
  }
};

/**
 * Отслеживание производительности API
 */
const trackPerformance = (metrics: ApiCallMetrics): void => {
  try {
    // Отправка в Google Analytics — только при наличии согласия на аналитику.
    if (
      typeof window !== "undefined" &&
      (window as any).gtag &&
      hasAnalyticsConsent()
    ) {
      const gtag = (window as any).gtag;

      gtag("event", "api_call", {
        event_category: "API",
        event_label: metrics.url,
        value: metrics.duration,
        non_interaction: true,
      });
    }

    // Или использовать Performance API для более детальной аналитики
    if (
      typeof window !== "undefined" &&
      window.performance &&
      window.performance.mark
    ) {
      const markName = `api-call-${metrics.url}-${Date.now()}`;
      window.performance.mark(markName);
    }
  } catch (trackingError) {
    console.warn("Failed to track performance:", trackingError);
  }
};

/**
 * Создать request interceptor с timing
 */
export const createRequestTimer = () => {
  return (config: any): any => {
    config.metadata = { startTime: Date.now() };
    return config;
  };
};

/**
 * Создать response interceptor с timing
 */
export const createResponseTimer = () => {
  return (response: AxiosResponse): AxiosResponse => {
    const metadata = (response.config as any).metadata;
    if (metadata) {
      metadata.endTime = Date.now();
    }
    logApiCall(response.config, response);
    return response;
  };
};

/**
 * Создать error interceptor с timing
 */
export const createErrorLogger = () => {
  return (error: AxiosError): Promise<AxiosError> => {
    const metadata = (error.config as any)?.metadata;
    if (metadata) {
      metadata.endTime = Date.now();
    }
    logApiCall(error.config || {}, undefined, error);
    return Promise.reject(error);
  };
};

const apiLogger = {
  logApiCall,
  createRequestTimer,
  createResponseTimer,
  createErrorLogger,
};

export default apiLogger;
